import { Outlet, useLocation } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { BreadcrumbTrail, PageHeader, Skeleton } from '@/components/ui'
import { useDocumentTitle } from '@/hooks'
import { projectPageTitle } from '@/lib/documentTitle'
import { useIsTrialAccount } from '@/modules/account'
import { ChatRailSkeleton, ChatSidebar } from '@/modules/chat'
import {
  CaseFactsDialog,
  DeleteProjectsDialog,
  EditCaseDialog,
  EditProjectDialog,
  projectSectionTitle,
  SupportedFilesDialog,
  TrialFinishedCelebration,
  TrialPhoneNotice,
  TrialProcessConfirmDialog,
  TrialTour,
  useFetchChecks,
  useFetchInsights,
  useFetchProject,
  useLoadingStatements,
  useProject,
  useWebsocket,
} from '@/modules/projects'
import { ProjectNav } from '@/modules/projects/components/ProjectNav'
import {
  hasAssemblyFailed,
  hasProjectSucceeded,
  isProjectAssembling,
} from '@/modules/projects/status'
import { FiltersProvider } from '@/stores'
import { getFolderPath } from '@/utils'
import { NewProjectWizard } from '../NewProjectWizard'
import { ProjectAssembling } from '../ProjectAssembling'
import { ProjectAssemblyFailed } from '../ProjectAssemblyFailed'
import HeaderActions from './HeaderActions'
import { useTrialFinish } from './useTrialFinish'

interface ProjectLayoutProps {
  slug: string
}

// Only the first statements load decides which screen the body shows, so a later reload never
// swaps the page out and back. Transactions are not waited on: the tab pages fetch them, and those
// pages are not shown until this decides.
const useFinishedFirstLoad = () => {
  const isLoadingStatements = useLoadingStatements()
  const [hasFinished, setHasFinished] = useState(false)

  useEffect(() => {
    if (!isLoadingStatements) setHasFinished(true)
  }, [isLoadingStatements])

  return hasFinished
}

export const ProjectLayout = ({ slug }: ProjectLayoutProps) => {
  useFetchProject(slug)
  // Fetched at the layout so the nav can hide the Checks tab when a project
  // has no checks, on any sub-page (not just the checks route).
  useFetchChecks(slug)
  // The header actions read insights, so load them on every sub-page.
  useFetchInsights(slug)
  useWebsocket()
  const trialFinish = useTrialFinish()
  const hasFinishedFirstLoad = useFinishedFirstLoad()
  const isTrial = useIsTrialAccount()
  const project = useProject()
  const { pathname } = useLocation()
  useDocumentTitle(
    projectPageTitle(projectSectionTitle(pathname, slug), project?.name),
  )

  const isAssembling = isProjectAssembling(project)
  const assemblyFailed = hasAssemblyFailed(project)

  const hasProjectContent =
    Boolean(project?.firstProcessedAt) ||
    hasProjectSucceeded(project) ||
    Boolean(project?.hasScreening)

  const showNewProjectWizard =
    project &&
    !hasProjectContent &&
    hasFinishedFirstLoad &&
    !isAssembling &&
    !assemblyFailed

  // Until the case and its files load, the body could become either the new project screen or a
  // tab, so it waits instead of showing one and swapping to the other.
  const isBodyUndecided =
    !project || (!hasProjectContent && !hasFinishedFirstLoad)

  const chatEnabled = Boolean(project?.isAiFeaturesEnabled)

  const renderBody = () => {
    if (isBodyUndecided) return <Skeleton className="h-96 w-full rounded-lg" />
    if (project && isAssembling) return <ProjectAssembling project={project} />
    if (project && assemblyFailed) {
      return <ProjectAssemblyFailed project={project} />
    }
    if (showNewProjectWizard) return <NewProjectWizard />

    return <Outlet />
  }

  return (
    <FiltersProvider contextId={`project-${slug}`}>
      <div className="flex flex-col gap-8">
        <DeleteProjectsDialog />
        <EditCaseDialog />
        <EditProjectDialog />
        <SupportedFilesDialog />
        <TrialProcessConfirmDialog />
        <TrialPhoneNotice />
        <TrialFinishedCelebration
          isShown={trialFinish.isCelebrating}
          onDone={trialFinish.finishCelebration}
        />
        {project && trialFinish.isTourRunning && (
          <TrialTour project={project} onEnd={trialFinish.endTour} />
        )}
        {chatEnabled && <CaseFactsDialog slug={slug} />}
        <div className="flex flex-col gap-6">
          <BreadcrumbTrail
            crumbs={[
              { label: 'Projects', to: '/projects' },
              project?.folder && {
                label: project.folder.name,
                to: getFolderPath(project.folder),
              },
            ]}
            page={project?.name || <Skeleton className="h-5 w-44" />}
          />

          <PageHeader
            title={project?.name || <Skeleton className="block h-9 w-72" />}
            subtitle={project?.folder?.name}
          >
            {project ? (
              !isAssembling &&
              !assemblyFailed && (
                <HeaderActions
                  project={project}
                  isWizardVisible={Boolean(showNewProjectWizard)}
                />
              )
            ) : (
              <Skeleton className="h-8 w-24 rounded-md" />
            )}
          </PageHeader>

          {hasProjectContent && <ProjectNav slug={slug} />}
          {!project && !isTrial && <Skeleton className="h-10 w-full" />}
        </div>

        <div>{renderBody()}</div>

        {!project && !isTrial && <ChatRailSkeleton />}
        {chatEnabled && project?.firstProcessedAt && (
          <ChatSidebar slug={slug} />
        )}
      </div>
    </FiltersProvider>
  )
}
